import React, { useState, useEffect, useCallback } from 'react';
import { trustService, caseService } from '../../services/api';
import { X, DollarSign, AlertCircle, Check } from 'lucide-react';
import useModal from '../../hooks/useModal';
import SearchableSelect from '../common/SearchableSelect';

const PAYMENT_METHODS = [
  { value: 'Check', label: 'Check' },
  { value: 'Wire Transfer', label: 'Wire Transfer' },
  { value: 'ACH', label: 'ACH / Bank Transfer' },
  { value: 'Other', label: 'Other' },
];

const WithdrawFromTrustModal = ({ isOpen, onClose, clientId, clientName, trustBalance, onWithdrawalRecorded }) => {
  const [formData, setFormData] = useState({
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    description: '',
    payment_method: '',
    reference_number: '',
    case_id: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cases, setCases] = useState([]);

  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);

  const fetchCases = useCallback(async () => {
    try {
      const casesResponse = await caseService.getCasesByClient(clientId);
      setCases(casesResponse.data || []);
    } catch (err) {
      console.error('Error fetching cases:', err);
    }
  }, [clientId]);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        amount: '',
        transaction_date: new Date().toISOString().split('T')[0],
        description: '',
        payment_method: '',
        reference_number: '',
        case_id: '',
        notes: ''
      });
      setError('');
      if (clientId) {
        fetchCases();
      }
    }
  }, [isOpen, clientId, fetchCases]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.amount || !formData.description) {
        setError('Amount and description are required');
        setLoading(false);
        return;
      }

      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount <= 0) {
        setError('Please enter a valid positive amount');
        setLoading(false);
        return;
      }

      if (trustBalance !== undefined && amount > trustBalance) {
        setError(`Amount exceeds available trust balance ($${trustBalance.toFixed(2)})`);
        setLoading(false);
        return;
      }

      const withdrawalData = {
        amount: amount,
        description: formData.description,
        payment_method: formData.payment_method || null,
        reference_number: formData.reference_number || null,
        case_id: formData.case_id || null,
        notes: formData.notes || null,
        transaction_date: formData.transaction_date || null
      };

      const result = await trustService.createClientWithdrawal(clientId, withdrawalData);

      if (onWithdrawalRecorded) {
        onWithdrawalRecorded(result.data);
      }

      onClose();
    } catch (err) {
      console.error('Error recording withdrawal:', err);
      setError(err.response?.data?.detail || 'Failed to record withdrawal');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-screen overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <DollarSign className="h-6 w-6 text-red-600 mr-2" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Withdraw from Trust</h2>
              {clientName && (
                <p className="text-sm text-gray-500">{clientName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {trustBalance !== undefined && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <span className="text-sm text-gray-600">Available balance: </span>
              <span className="text-sm font-semibold text-green-600">
                ${trustBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                onWheel={(e) => e.target.blur()}
                step="0.01"
                min="0.01"
                required
                placeholder="0.00"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date
            </label>
            <input
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              maxLength={500}
              placeholder="e.g. Client refund, Fee disbursement"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Method
            </label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">Select method (optional)</option>
              {PAYMENT_METHODS.map(method => (
                <option key={method.value} value={method.value}>{method.label}</option>
              ))}
            </select>
          </div>

          {/* Reference Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reference Number
            </label>
            <input
              type="text"
              name="reference_number"
              value={formData.reference_number}
              onChange={handleInputChange}
              maxLength={100}
              placeholder="Check #, wire ID, confirmation #, etc."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {/* Associated Case */}
          {cases.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Associated Case (Optional)
              </label>
              <SearchableSelect
                value={formData.case_id}
                onChange={(val) => setFormData(prev => ({ ...prev, case_id: val }))}
                options={cases.map(c => ({
                  value: c.id,
                  label: `${c.case_number} - ${c.title}`
                }))}
                emptyOption="Select a case (optional)"
              />
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={2}
              maxLength={1000}
              placeholder="Additional details..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {/* Preview */}
          {formData.amount && (
            <div className="bg-red-50 rounded-lg p-4 border border-red-200">
              <div className="text-sm text-gray-700">
                <span className="font-medium text-red-700 text-lg">
                  -${parseFloat(formData.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="ml-2 text-gray-500">will be withdrawn from trust</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.amount || !formData.description}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Withdrawing...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Withdraw from Trust
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WithdrawFromTrustModal;
